import { createContext, useContext } from 'react'

/** What the user chose. `system` follows the OS and keeps following it. */
export type ThemePreference = 'light' | 'dark' | 'system'

/** What is actually painted right now. */
export type ResolvedTheme = 'light' | 'dark'

export type ThemeContextValue = {
  preference: ThemePreference
  resolved: ResolvedTheme
  setPreference: (preference: ThemePreference) => void
}

export const THEME_STORAGE_KEY = 'avexa.theme'

/**
 * What a first-time visitor gets.
 *
 * Light, not 'system'. The brand is blue on a pale ground and the marketing
 * copy was written for it; a visitor whose laptop happens to be in dark mode
 * should not meet a different product on their first visit. Anyone who prefers
 * dark can switch, and the choice is remembered in localStorage from then on.
 */
export const DEFAULT_THEME: ThemePreference = 'light'

/** Toggle order. Light first, because that is where everyone starts. */
export const THEME_PREFERENCES: readonly ThemePreference[] = ['light', 'dark', 'system']

export const ThemeContext = createContext<ThemeContextValue | null>(null)

export const useTheme = (): ThemeContextValue => {
  const context = useContext(ThemeContext)

  if (!context) {
    throw new Error('useTheme must be used inside <ThemeProvider>')
  }

  return context
}
